import {useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {Heading} from '@/components/tailwind/heading'
import {Text} from '@/components/tailwind/text'
import {Input, InputGroup} from '@/components/tailwind/input'
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation, useCommonTranslation} from '@/i18n/hooks'
import {BankDataValidationEntry, BankDataValidationType} from '@/models/bank-data-validation'
import {getBankDataValidation} from '@/services/bank-data-validation-services'
import {BankDataValidationDataTable} from './bank-data-validation-data-table'
import {useBankDataValidationColumns, useBankDataValidationColumnsMissingAccount} from './bank-data-validation-columns'
import log from '@/lib/logger'

function BankDataValidationListPage() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [data, setData] = useState<BankDataValidationEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    
    // Read validation type from URL, default to INVALID_DATA
    const urlType = searchParams.get('type')
    const validationType: BankDataValidationType = 
        urlType === 'MISSING_ACCOUNT' ? 'MISSING_ACCOUNT' : 'INVALID_DATA'
    
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {t: tCommon} = useCommonTranslation()
    
    const columnsInvalidData = useBankDataValidationColumns()
    const columnsMissingAccount = useBankDataValidationColumnsMissingAccount()

    function setValidationType(type: BankDataValidationType) {
        const params = new URLSearchParams(searchParams)
        if (type === 'MISSING_ACCOUNT') {
            params.set('type', 'MISSING_ACCOUNT')
        } else {
            params.delete('type')
        }
        setSearchParams(params)
    }

    useEffect(() => {
        async function fetchData() {
            if (!token) {
                log.error('No token available for fetching bank data validation')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                const result = await getBankDataValidation(token, validationType)
                setData(result)
            } catch (err) {
                log.error('Error fetching bank data validation:', err)
                setError(t('bankDataValidation.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [token, validationType]) // eslint-disable-line react-hooks/exhaustive-deps

    const columns = validationType === 'INVALID_DATA'
        ? columnsInvalidData
        : columnsMissingAccount

    if (loading) {
        return (
            <div className="container mx-auto py-6">
                <Text>{tCommon('loading')}</Text>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto py-6">
                <Text className="text-red-500">{error}</Text>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div>
                <Heading>{t('bankDataValidation.title')}</Heading>
                <Text>{t('bankDataValidation.description')}</Text>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700 p-0.5">
                    <button
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            validationType === 'INVALID_DATA'
                                ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                                : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
                        }`}
                        onClick={() => setValidationType('INVALID_DATA')}
                        data-testid="filter-invalid-data"
                    >
                        {t('bankDataValidation.filter.invalidData')}
                    </button>
                    <button
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            validationType === 'MISSING_ACCOUNT'
                                ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                                : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
                        }`}
                        onClick={() => setValidationType('MISSING_ACCOUNT')}
                        data-testid="filter-missing-account"
                    >
                        {t('bankDataValidation.filter.missingAccount')}
                    </button>
                </div>

                <div className="flex-1">
                    <InputGroup>
                        <MagnifyingGlassIcon/>
                        <Input
                            type="text"
                            placeholder={t('bankDataValidation.search.placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label={t('bankDataValidation.search.ariaLabel')}
                        />
                    </InputGroup>
                </div>
            </div>

            {data.length === 0 ? (
                <Text className="italic text-gray-500 dark:text-gray-400">{t('bankDataValidation.noResults')}</Text>
            ) : (
                <BankDataValidationDataTable
                    columns={columns}
                    data={data}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    )
}

export default BankDataValidationListPage
